/**
 * Default landing route per role. Lives apart from `roleMenus.config.tsx` (which pulls in dozens of
 * MUI icons) so the entry chunk — AuthContext, the landing page — can resolve a role's home route
 * without downloading the whole menu configuration.
 */

/**
 * Ordered default route resolution.
 * Move entries to change priority without touching logic.
 */
const DEFAULT_ROUTE_BY_ROLE: Array<{ role: string; path: string }> = [
  { role: 'ADMIN', path: '/backoffice/dashboard' },
  { role: 'TRAINER', path: '/trainer/participants' },
  { role: 'MENTOR', path: '/mentor/dashboard' },
  { role: 'MANAGER', path: '/manager/dashboard' },
  { role: 'COMPANY_ADMIN', path: '/company/dashboard' },
  { role: 'PARTICIPANT', path: '/participant/dashboard' },
];

/** True when `roleNames` contains `roleName` with or without the `ROLE_` prefix. */
export const hasNormalizedRole = (roleNames: string[], roleName: string): boolean =>
  roleNames.includes(roleName) ||
  roleNames.includes(`ROLE_${roleName}`) ||
  roleNames.includes(roleName.replace('ROLE_', ''));

/**
 * Get the default route for the active role.
 * Legacy collection inputs are retained for compatibility, but only the first
 * value is considered.
 * @param roles - Active role name or a legacy single-value collection
 * @returns Default route path for the user
 */
export const getDefaultRoute = (roles?: Set<string> | string[] | string): string => {
  if (!roles) return '/';

  const roleNames = typeof roles === 'string'
    ? [roles]
    : (Array.isArray(roles) ? roles : Array.from(roles)).slice(0, 1);

  for (const { role, path } of DEFAULT_ROUTE_BY_ROLE) {
    if (hasNormalizedRole(roleNames, role)) {
      return path;
    }
  }

  return '/';
};
